import { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useBuildingKey, getAllUniqueRiskTypesFromRoom, getAllUniqueHazardsFromRoom, RiskConstructionData, HazardData } from '../../Helpers/utils';
import { BaseComponentImpl, RoomImpl, LevelImpl } from '../../types/implementations';
import { useBuilding } from '../../Helpers/BuildingContext';
import { BaseType } from '../../types/Enums';
import { getAllComponentsForRooms } from '../../Helpers/ComponentRoomUtils';
import { Building, PhotoModel, BlueprintLocation } from '../../types/protocols';
import { roomLevelKeys } from '../../Helpers/roomUtils';

export interface RoomLevelDrawing {
    level: LevelImpl;
    blueprint: PhotoModel | undefined;
    location: BlueprintLocation | undefined;
}

interface UseRoomReturn {
    buildingKey: string | null;
    roomKey: string | undefined;
    /** The other levels of a room spanning several, lowest first, each with its current drawing and the room's pin on it (multi-level rooms R16). */
    otherLevelDrawings: RoomLevelDrawing[];
    room: RoomImpl | null;
    level: LevelImpl | undefined;
    currentBuilding: Building | null;
    loadingBuilding: boolean;
    buildingError: string | null;
    currentBlueprint: PhotoModel | undefined;
    roomBlueprintLocation: BlueprintLocation | undefined;
    utilities: BaseComponentImpl[];
    fixedFurnishings: BaseComponentImpl[];
    membranes: BaseComponentImpl[];
    windows: BaseComponentImpl[];
    doors: BaseComponentImpl[];
    ceilingComponents: BaseComponentImpl[];
    wallComponents: BaseComponentImpl[];
    floorComponents: BaseComponentImpl[];
    totalCeilingArea: number;
    totalWallArea: number;
    totalFloorArea: number;
    totalCeilingAreaExcludingOpenings: number;
    totalWallAreaExcludingOpenings: number;
    totalFloorAreaExcludingOpenings: number;
    riskConstructions: RiskConstructionData[];
    hazards: HazardData[];
}

export const useRoom = (): UseRoomReturn => {
    const buildingKey = useBuildingKey();
    const { roomId: roomKey } = useParams<{ roomId: string }>();
    const [room, setRoom] = useState<RoomImpl | null>(null);
    const [level, setLevel] = useState<LevelImpl | undefined>(undefined);
    const [currentBlueprint, setCurrentBlueprint] = useState<PhotoModel | undefined>(undefined);

    const { currentBuilding, buildingError, loadingBuilding, fetchBuilding } = useBuilding();

    useEffect(() => {
        if (buildingKey) {
            fetchBuilding(buildingKey);
        }
    }, [buildingKey, fetchBuilding]);

    useEffect(() => {
        if (currentBuilding?.model.structure?.rooms && !loadingBuilding && roomKey) {
            const roomData = currentBuilding.model.structure.rooms[roomKey];
            if (roomData) {
                const newRoom = new RoomImpl(roomData);
                setRoom(newRoom);
                const foundLevel = currentBuilding.model.structure?.levels?.[roomData.levelKey || ''];
                setLevel(foundLevel ? new LevelImpl(foundLevel) : undefined);

                if (foundLevel) {
                    if (foundLevel.currentBlueprintKey && foundLevel.blueprints?.[foundLevel.currentBlueprintKey]) {
                        setCurrentBlueprint(foundLevel.blueprints[foundLevel.currentBlueprintKey]);
                    } else {
                        setCurrentBlueprint(undefined);
                    }
                }
            }
        }
    }, [currentBuilding, roomKey, loadingBuilding]);

    // All components for this room
    const allRoomComponents = useMemo(() => {
        if (!currentBuilding || !roomKey) return [];
        return getAllComponentsForRooms(currentBuilding, [roomKey]);
    }, [currentBuilding, roomKey]);

    // Components filtered by type
    const utilities = useMemo(() => allRoomComponents.filter(c => c.type === BaseType.Utility), [allRoomComponents]);
    const fixedFurnishings = useMemo(() => allRoomComponents.filter(c => c.type === BaseType.FixedFurnishing), [allRoomComponents]);
    const membranes = useMemo(() => allRoomComponents.filter(c => c.type === BaseType.Membrane), [allRoomComponents]);
    const windows = useMemo(() => allRoomComponents.filter(c => c.type === BaseType.Window), [allRoomComponents]);
    const doors = useMemo(() => allRoomComponents.filter(c => c.type === BaseType.Door), [allRoomComponents]);
    const ceilingComponents = useMemo(() => allRoomComponents.filter(c => c.type === BaseType.Ceiling), [allRoomComponents]);
    const wallComponents = useMemo(() => allRoomComponents.filter(c => c.type === BaseType.Wall), [allRoomComponents]);
    const floorComponents = useMemo(() => allRoomComponents.filter(c => c.type === BaseType.Floor), [allRoomComponents]);

    // Area calculations
    const totalCeilingArea = useMemo(() => {
        if (!currentBuilding) return 0;
        return ceilingComponents.reduce((total, c) => total + (c.totalArea(currentBuilding) || 0), 0);
    }, [ceilingComponents, currentBuilding]);
    const totalWallArea = useMemo(() => {
        if (!currentBuilding) return 0;
        return wallComponents.reduce((total, c) => total + (c.totalArea(currentBuilding) || 0), 0);
    }, [wallComponents, currentBuilding]);
    const totalFloorArea = useMemo(() => {
        if (!currentBuilding) return 0;
        return floorComponents.reduce((total, c) => total + (c.totalArea(currentBuilding) || 0), 0);
    }, [floorComponents, currentBuilding]);

    // Area calculations excluding openings (doors/windows)
    const totalCeilingAreaExcludingOpenings = useMemo(() => {
        if (!currentBuilding) return 0;
        return ceilingComponents.reduce((total, c) =>
            total + (c.totalAreaExcludingWindowsAndDoors(currentBuilding) || 0), 0
        );
    }, [ceilingComponents, currentBuilding]);

    const totalWallAreaExcludingOpenings = useMemo(() => {
        if (!currentBuilding) return 0;
        return wallComponents.reduce((total, c) =>
            total + (c.totalAreaExcludingWindowsAndDoors(currentBuilding) || 0), 0
        );
    }, [wallComponents, currentBuilding]);

    const totalFloorAreaExcludingOpenings = useMemo(() => {
        if (!currentBuilding) return 0;
        return floorComponents.reduce((total, c) =>
            total + (c.totalAreaExcludingWindowsAndDoors(currentBuilding) || 0), 0
        );
    }, [floorComponents, currentBuilding]);

    // Blueprint location for this room
    const roomBlueprintLocation = useMemo(() =>
        level?.currentBlueprintKey && room
            ? room.getBlueprintLocation(level.currentBlueprintKey)
            : undefined,
        [level?.currentBlueprintKey, room]
    );

    const otherLevelDrawings = useMemo((): RoomLevelDrawing[] => {
        const levels = currentBuilding?.model.structure?.levels;
        if (!room || !levels) return [];
        return roomLevelKeys(room, levels)
            .filter(key => key !== room.levelKey)
            .map(key => new LevelImpl(levels[key]))
            .sort((a, b) => a.number - b.number || a.key.localeCompare(b.key))
            .map(levelImpl => {
                const blueprintKey = levelImpl.currentBlueprintKey;
                return {
                    level: levelImpl,
                    blueprint: blueprintKey ? levelImpl.blueprints?.[blueprintKey] : undefined,
                    location: blueprintKey ? room.getBlueprintLocation(blueprintKey) : undefined,
                };
            });
    }, [currentBuilding, room]);

    // Risk constructions and hazards
    const riskConstructions = useMemo(() => {
        if (!currentBuilding || !roomKey) return [];
        return getAllUniqueRiskTypesFromRoom(currentBuilding, roomKey);
    }, [currentBuilding, roomKey]);

    const hazards = useMemo(() => {
        if (!currentBuilding || !roomKey) return [];
        return getAllUniqueHazardsFromRoom(currentBuilding, roomKey);
    }, [currentBuilding, roomKey]);

    return {
        buildingKey,
        roomKey,
        room,
        level,
        currentBuilding,
        loadingBuilding,
        buildingError,
        currentBlueprint,
        roomBlueprintLocation,
        otherLevelDrawings,
        // Components by type
        utilities,
        fixedFurnishings,
        membranes,
        windows,
        doors,
        ceilingComponents,
        wallComponents,
        floorComponents,
        // Areas
        totalCeilingArea,
        totalWallArea,
        totalFloorArea,
        totalCeilingAreaExcludingOpenings,
        totalWallAreaExcludingOpenings,
        totalFloorAreaExcludingOpenings,
        // Risks
        riskConstructions,
        hazards,
    };
};
